import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import QueryString from 'query-string';
import { useNotify, useService } from '../../../../../hooks';
import FileService, {
  type GetFileResponse,
} from '../../../../../services/FileService';
import {
  useReportErrorToBugsnag,
  useReportInfoToBugsnag,
} from '../../../../../util/bugsnagReporting';
import {
  type KnowledgeBaseCitation,
  isBrowserPreviewableKnowledgeBaseMime,
} from '../../../../../util/knowledgeBaseCitation';

const fileService = new FileService();

const messages = defineMessages({
  knowledgeBaseFileError: {
    id: 'phc-web-toolkit.subjectViewer.attachments.knowledgeBaseCitationInline.error',
    defaultMessage:
      'Could not open this knowledge base file. Try again or download from Files.',
  },
});

/**
 * Resolves a knowledge-base S3 citation to a file-service file and opens it in a
 * new tab. Mirrors the qa-agent chat citation handling so inline citations and
 * attachment-list entries share the same "link to the file-service file" behavior.
 *
 * The browser resolves the file id via a name+datasetId lookup at click time —
 * nothing about a file UUID is encoded in the citation — then falls back to a
 * signed download link for mimes the browser cannot preview.
 */
export const useOpenKnowledgeBaseFile = (citation: KnowledgeBaseCitation) => {
  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const reportErrorToBugsnag = useReportErrorToBugsnag();
  const reportInfoToBugsnag = useReportInfoToBugsnag();

  const [fetchFiles] = useService(fileService.fetchFiles);
  const [fetchOpenLink] = useService(fileService.fetchOpenLink);
  const [fetchDownloadLink] = useService(fileService.fetchDownloadLink);

  return React.useCallback(async () => {
    try {
      const list = await fetchFiles({
        datasetId: citation.projectId,
        name: citation.name,
        pageSize: 1,
      });
      const file = list.data?.items?.find(
        (item: GetFileResponse) => item.name === citation.name,
      );
      if (!file?.id) {
        notify(formatMessage(messages.knowledgeBaseFileError), 'error');
        return;
      }
      const res = await fetchOpenLink(file.id);
      const downloadUrl = res.data?.downloadUrl;
      if (!downloadUrl) {
        notify(formatMessage(messages.knowledgeBaseFileError), 'error');
        return;
      }
      const effectiveMime = res.data?.contentType || file.contentType;
      if (!isBrowserPreviewableKnowledgeBaseMime(effectiveMime)) {
        reportInfoToBugsnag(
          'Knowledge base citation mime not shown in browser; download fallback',
          {
            metaData: {
              knowledgeBase: {
                mime: effectiveMime,
                projectId: citation.projectId,
                name: citation.name,
              },
            },
          },
        );
        const dl = await fetchDownloadLink(file.id);
        const dlUrl = dl.data?.downloadUrl;
        if (dlUrl) {
          window.open(dlUrl);
        } else {
          notify(formatMessage(messages.knowledgeBaseFileError), 'error');
        }
        return;
      }
      const mime = (effectiveMime || '').toLowerCase();
      let url = downloadUrl;
      if (mime === 'application/pdf' && citation.page) {
        url = `${downloadUrl}#${QueryString.stringify({
          page: citation.page,
        })}`;
      }
      window.open(url);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      reportErrorToBugsnag(err, {
        metaData: {
          knowledgeBase: { projectId: citation.projectId, name: citation.name },
        },
      });
      notify(formatMessage(messages.knowledgeBaseFileError), 'error');
    }
  }, [
    citation.name,
    citation.page,
    citation.projectId,
    fetchDownloadLink,
    fetchFiles,
    fetchOpenLink,
    formatMessage,
    notify,
    reportErrorToBugsnag,
    reportInfoToBugsnag,
  ]);
};
